import { MainHeader, SectionParagraph } from '@/components/shared/Content';
import { type UseContentProps, useContent } from '@/hooks/useContent';
import qrCode from '@/static/assets/policy_two/qr_code.png';
import { Tooltip as CDKTooltip, Flex, font } from '@lemonade-hq/cdk';
import { BackIcon as Arrow, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import type React from 'react';
import { memo, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  em {
    font-style: italic;
  }
`;

const TooltipLink = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const StyledCDKTooltip = styled(CDKTooltip)`
  ${MEDIA_QUERIES.mobile`
    max-width: calc(100vw - 50px);
    text-wrap: pretty;
  `}
`;

const Tooltip: React.FC<React.PropsWithChildren<{ content: string }>> = ({ children, content }) => {
  const theme = useTheme();
  return (
    <StyledCDKTooltip
      alignment="bottom"
      content={content}
      tooltipBackgroundColor={theme.colors.night}
      tooltipBorderRadius={5}
      tooltipColor={theme.colors.white}
      tooltipArrowSize={0}
    >
      <TooltipLink>{children}</TooltipLink>
    </StyledCDKTooltip>
  );
};

const Heading1 = styled(MainHeader)`
  margin-bottom: 30px;
  text-align: left;
  ${MEDIA_QUERIES.mobile`
    word-wrap: break-word;
  `}
`;

const Text = styled(SectionParagraph)`
  text-align: left;
  margin-bottom: 20px;
  font-size: 1.6rem;
  text-wrap: pretty;
  strong {
    font-weight: 700;
  }
`;

const Heading2 = styled.h2`
  ${font('main', { fontSize: '3.2rem', fontWeight: 700 })}
  text-align: left;
  margin-bottom: 20px;
`;

const Heading3 = styled.h3`
  ${font('main', { fontSize: '2.1rem', fontWeight: 700 })}
  margin-bottom: 20px;
`;

const Heading4 = styled.h4`
  ${font('main', { fontSize: '1.8rem', fontWeight: 700 })}
  text-align: left;
  margin-bottom: 15px;
`;

const BulletList = styled.ul`
  ${font('main', { fontSize: '1.8rem' })}
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style-type: none;
  margin-left: 20px;
  margin-bottom: 20px;

  li::before {
    font-size: 2.2rem;
    content: '•';
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }
`;

const OrderedList = styled.ol`
  ${font('main', { fontSize: '1.8rem' })}
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style-type: none;
  margin-left: 18px;
  margin-bottom: 20px;
  list-style-type: decimal;
`;

const Page = styled(Flex).attrs({})<{ ribbon?: 'dark' | 'light' }>`
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  width: min(100%, 820px);
  height: auto;
  min-height: 600px;
  background: white;
  border-radius: 5px;
  padding: 40px 20px 55px 20px;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 149.8px 299.6px 0px;
  border: 1px solid #ececec;
  overflow: hidden;
  gap: 60px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    min-height: 1120px;
    padding: 70px 90px;
  `}

  &::after {
    content: attr(data-page-number);
    ${font('main', { fontSize: '1.2rem' })}
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);

    ${MEDIA_QUERIES.tabletAndDesktop`
      bottom: 30px;
    `}
  }

  ${({ ribbon }) =>
    ribbon &&
    css`
      &::before {
        ${font('main', { fontSize: '1.2rem', fontWeight: 700 })};
        color: white;
        padding: 10px 0;
        width: 200px;
        text-align: center;
        position: absolute;
        top: 12px;
        right: -70px;
        transform: rotate(45deg);
        transform-origin: 50% 50%;
        text-transform: uppercase;
        content: attr(data-ribbon-text-xs);
        background: ${ribbon === 'dark' ? '#4a4a4a' : '#b7b7b7'};
        border: solid 2px #ececec;

        ${MEDIA_QUERIES.tabletAndDesktop`
          top: 30px;
          left: -55px;
          content: attr(data-ribbon-text-lg);
          transform: rotate(-45deg);
          transform-origin: 50% 50%;
        `};
      }
    `}

  &:first-of-type h1 {
    ${font('main', { fontSize: '4rem', fontWeight: 700, lineHeight: 1.3 })}
    margin-top: 0;

    ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '9rem', fontWeight: 700, lineHeight: 1.1 })}
  `}
  }

  &:first-of-type h2 {
    ${font('main', { fontSize: '2rem', fontWeight: 300 })}

    ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '2.4rem', fontWeight: 300 })}
  `}
  }
`;

const SubtitleLight = styled.h3`
  ${font('main', { fontSize: '2.1rem', fontWeight: 300 })}
  margin-bottom: 20px;
`;

const Disclaimer = styled.div`
  ${font('main', { fontSize: '1.4rem' })}
  text-align: center;
  margin-top: 40px;
`;

const Tip = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  position: relative;
  &::after {
    opacity: 0;
    visibility: hidden;
    ${font('main', { fontSize: '1.2rem' })};
    content: attr(data-tip);
    position: absolute;
    bottom: 110%;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.colors.textPrimary};
    color: white;
    padding: 5px 10px;
    border-radius: 5px;
    white-space: nowrap;
    z-index: 1;
    transition: all ease 250ms;
  }

  &:hover::after {
    opacity: 1;
    bottom: 100%;
    visibility: visible;
  }
`;

const Menu = styled.ul`
  ${font('main', { fontSize: '1.6rem' })}
  display: flex;
  align-items: center;
  gap: 20px;
`;

const GrayBox = styled.div`
  background: #fbfbfb;
  border-radius: 8px;
  border: solid 2px #dbdbdb;
  padding: 30px 30px 0 30px;
`;

const GoToTop = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  left: 50%;
  margin-left: 440px;
  border-radius: 30px;
  height: 60px;
  width: 60px;
  border: ${({ theme }) => `solid 1px ${theme.colors.pebble}`};
  background: ${({ theme }) => `solid 1px ${theme.colors.aliceBlue}`};
  cursor: pointer;
`;

const QrCodeImage: React.FC = () => {
  return (
    <Image
      src={qrCode}
      width={96}
      height={96}
      alt="Lemonade app, car insurance id card"
      style={{ pointerEvents: 'none', marginBottom: '20px' }}
    />
  );
};

const FooterMenu: React.FC = () => {
  return (
    <Flex flexDirection="column" alignItems="center" mt="30px">
      <Heading4>Learn More About Lemonade:</Heading4>
      <Menu>
        <li>
          <Link href="/claims">Claims</Link>
        </li>
        <li>
          <Link href="/giveback">Giveback</Link>
        </li>
        <li>
          <Link href="/lemonade-goes-global">{`Lemonade's US Rollout`}</Link>
        </li>
      </Menu>
    </Flex>
  );
};

const components = {
  h1: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading1 {...props} />,
  h2: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading2 {...props} />,
  h3: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading3 {...props} />,
  h4: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading4 {...props} />,
  p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => <Text {...props} />,
  ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <BulletList {...props} />,
  ol: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <OrderedList {...props} />,
  QrCodeImage,
  Page,
  Tip,
  SubtitleLight,
  GrayBox,
  FooterMenu,
  Disclaimer,
  Tooltip,
};

const PolicyTwoPageContentComponent: React.FC<UseContentProps> = ({ content }) => {
  const theme = useTheme();
  const Component = useContent({ content });

  const { ref, entry } = useInView({ threshold: 0.1 });

  const showShareOptionsAndGoToTop = useMemo(() => {
    return (entry?.intersectionRect?.top ?? 0) === 0;
  }, [entry]);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <GlobalStyle />
      <div ref={ref} />
      <Flex
        alignItems="center"
        flexDirection="column"
        gap="15px"
        width="100%"
        pt={[100, 100, 120]}
        id="policy-sample"
        pb={[40, 70]}
      >
        <Component components={components} />
        <AnimatePresence>
          {showShareOptionsAndGoToTop && (
            <GoToTop
              onClick={handleScrollToTop}
              animate={{
                opacity: [0, 1],
                y: [40, 0],
                scale: [0.8, 1],
              }}
              transition={{
                duration: 0.25,
              }}
            >
              <Arrow
                colorOverride={theme.colors.night}
                style={{
                  transform: 'rotate(90deg)',
                }}
              />
            </GoToTop>
          )}
        </AnimatePresence>
      </Flex>
    </>
  );
};

export const PolicyTwoPageContent = memo(PolicyTwoPageContentComponent);
